import { DefaultScreen } from '@/components/template/default-screen';
import { STRINGS } from '@/constants/strings';
import CoGuardianProfileScreen from '@/features/guardian-player/screens/co-guardian-profile-screen';
import GuardianCircleScreen from '@/features/guardian-player/screens/guardian-circle-screen';
import { GuardianCoachMaterialWebViewScreen } from '@/features/guardian-player/screens/guardian-coach-material-webview-screen';
import { GuardianCoachMaterialsScreen } from '@/features/guardian-player/screens/guardian-coach-materials-screen';
import { GuardianPlayerLevelDetailScreen } from '@/features/guardian-player/screens/guardian-player-level-detail-screen';
import { GuardianPlayerSkillDetailScreen } from '@/features/guardian-player/screens/guardian-player-skill-detail-screen';
import InvitationSentScreen from '@/features/guardian-player/screens/invitation-sent-screen';
import InviteGuardianScreen from '@/features/guardian-player/screens/invite-guardian-screen';
import LeaveCircleScreen from '@/features/guardian-player/screens/leave-circle-screen';
import PlayerDetailScreen from '@/features/guardian-player/screens/player-detail-screen';
import PlayerKitNotesScreen from '@/features/guardian-player/screens/player-kit-notes-screen';
import PlayerProgressTrackerScreen from '@/features/guardian-player/screens/player-progress-tracker-screen';
import PlayerSessionDetailScreen from '@/features/guardian-player/screens/player-session-detail-screen';
import PlayerSessionHistoriesScreen from '@/features/guardian-player/screens/player-session-histories-screen';
import RemoveCoGuardianScreen from '@/features/guardian-player/screens/remove-co-guardian-screen';
import {
  GuardianAccountSecuritiesScreen,
  GuardianContactUsScreen,
  GuardianDeleteAccountScreen,
  GuardianHelpAndSupportScreen,
  GuardianNotificationPreferencesScreen,
  GuardianPrivacyLegalScreen,
  GuardianProfileEditScreen,
} from '@/features/guardian-profile/screens';
import { GuardianChangePasswordScreen } from '@/features/guardian-profile/screens/guardian-change-password-screen';
import { AddPlayerScreen } from '@/features/guardian/screens/add-player-screen';
import { GuardianFamilyCalendarDayScreen } from '@/features/guardian/screens/family-calendar-day-screen';
import { GuardianFamilyCalendarScreen } from '@/features/guardian/screens/family-calendar-screen';
import { GuardianEventDetailScreen } from '@/features/guardian/screens/guardian-event-detail-screen';
import { GuardianNotificationsScreen } from '@/features/guardian/screens/guardian-notifications-screen';
import { useScaling } from '@/hooks/use-scaling';
import { OnboardingFinishScreen } from '@/features/onboarding/screens/finish-screen';
import { OnboardingScreen } from '@/features/onboarding/screens/onboarding-screen';
import GuardianTabNavigator from '@/navigations/guardian-stack/guardian-tab-navigator';
import type { GuardianStackParamList } from '@/navigations/types';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import React from 'react';

const Stack = createNativeStackNavigator<GuardianStackParamList>();

/**
 * Guardian flow — skeleton.
 *
 * Guardians land on the onboarding wizard right after login; finishing or skipping
 * it moves on to the dashboard. Will grow to mirror the coach `AuthStack`
 * (drawer + tabs + modals) as the feature is implemented.
 */
export function GuardianStack() {
  const { isTablet } = useScaling();

  return (
    <Stack.Navigator screenOptions={{ headerShown: false, title: STRINGS.appDocumentTitle }}>
      <Stack.Screen name="Onboarding" component={OnboardingScreen} />
      <Stack.Screen name="OnboardingFinish" component={OnboardingFinishScreen} />
      <Stack.Screen name="GuardianDashboard" component={GuardianTabNavigator} />
      <Stack.Screen name="AddPlayer" component={AddPlayerScreen} />
      <Stack.Screen
        name="GuardianNotifications"
        component={GuardianNotificationsScreen}
        options={
          isTablet
            ? { presentation: 'transparentModal', animation: 'fade' }
            : { presentation: 'card' }
        }
      />
      {/* TODO: Will add those screen to PlayerDetailStack */}
      <Stack.Screen name="PlayerProgressTracker" component={PlayerProgressTrackerScreen} />
      <Stack.Screen name="GuardianPlayerLevelDetail" component={GuardianPlayerLevelDetailScreen} />
      <Stack.Screen name="PlayerSessionHistories" component={PlayerSessionHistoriesScreen} />
      <Stack.Screen name="PlayerSessionDetail" component={PlayerSessionDetailScreen} />
      <Stack.Screen name="GuardianBookingDetails" component={DefaultScreen} />
      <Stack.Screen name="GuardianPlayerSkillDetail" component={GuardianPlayerSkillDetailScreen} />
      <Stack.Screen name="GuardianCoachMaterials" component={GuardianCoachMaterialsScreen} />
      <Stack.Screen
        name="GuardianCoachMaterialWebView"
        component={GuardianCoachMaterialWebViewScreen}
      />
      <Stack.Screen name="GuardianAccountSecurities" component={GuardianAccountSecuritiesScreen} />
      <Stack.Screen
        name="GuardianNotificationPreferences"
        component={GuardianNotificationPreferencesScreen}
        options={({ route }) =>
          route.params?.source === 'notification-center'
            ? { presentation: 'fullScreenModal' }
            : { presentation: 'card' }
        }
      />
      <Stack.Screen name="GuardianChangePassword" component={GuardianChangePasswordScreen} />
      <Stack.Screen name="GuardianHelpAndSupport" component={GuardianHelpAndSupportScreen} />
      <Stack.Screen name="GuardianContactUs" component={GuardianContactUsScreen} />
      <Stack.Screen name="PlayerDetail" component={PlayerDetailScreen} />
      <Stack.Screen name="PlayerKitNotes" component={PlayerKitNotesScreen} />
      <Stack.Screen name="GuardianProfileEdit" component={GuardianProfileEditScreen} />
      <Stack.Screen name="GuardianDeleteAccount" component={GuardianDeleteAccountScreen} />
      <Stack.Screen name="GuardianPrivacyLegal" component={GuardianPrivacyLegalScreen} />
      <Stack.Screen name="GuardianCircle" component={GuardianCircleScreen} />
      <Stack.Screen name="CoGuardianProfile" component={CoGuardianProfileScreen} />
      <Stack.Screen name="InviteGuardian" component={InviteGuardianScreen} />
      <Stack.Screen name="InvitationSent" component={InvitationSentScreen} />
      <Stack.Screen name="LeaveCircle" component={LeaveCircleScreen} />
      <Stack.Screen name="RemoveCoGuardian" component={RemoveCoGuardianScreen} />
      <Stack.Screen name="GuardianFamilyCalendar" component={GuardianFamilyCalendarScreen} />
      <Stack.Screen name="GuardianFamilyCalendarDay" component={GuardianFamilyCalendarDayScreen} />
      <Stack.Screen
        name="GuardianEventDetail"
        component={GuardianEventDetailScreen}
        options={{ presentation: 'card', animation: 'slide_from_right' }}
      />
      <Stack.Screen name="ComingSoon" component={DefaultScreen} />
    </Stack.Navigator>
  );
}
